import PencilIcon from 'mdi-react/PencilIcon'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'redux'

import Input from '@/components/form/Input'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { activatePopup } from '@/ducks/popupReducer'
import {
  addProduct,
  createProductSelection,
  getIsCompleted,
  getIsLeasing,
  getOrderTotalCost,
  getSelectedProductsMapped,
  getTempOrderName,
  setTempOrderName,
} from '@/ducks/shopOrderReducer'
import withShopProductsLoaded from '@/hocs/withShopProductsLoaded'

import ProductRemovalDialog from './steps/ProductRemovalDialog'

import './quote-summary.scss'

const QuoteSummary = () => {
  const dispatch = useDispatch()
  const selectedProducts = useSelector(getSelectedProductsMapped)
  const isLeasing = useSelector(getIsLeasing)
  const orderName = useSelector(getTempOrderName)

  return (
    <div className="shop-content__summary">
      <h2>
        <Translation id="Quote Summary" />
      </h2>

      <div className="quote-name">
        {orderName || 'New quote'}

        <PencilIcon />
      </div>

      <Input
        className="order-name"
        placeholder="New quote"
        onChange={e => dispatch(setTempOrderName(e.target.value))}
        value={orderName}
      />

      {selectedProducts.length === 0 && (
        <p>
          <Translation id="No products selected" />
        </p>
      )}

      <table>
        <tbody>
          {/* @ts-ignore */}
          {selectedProducts.map(product => (
            <Product key={product.id} {...product} />
          ))}
        </tbody>

        <tbody>
          <tr>
            <td className="spacer" />
          </tr>
        </tbody>
        {/* @ts-ignore */}
        {isLeasing ? <LeasingTotals /> : <BuyingTotals />}
      </table>
    </div>
  )
}

const LeasingTotals = () => {
  const selectedProducts = useSelector(getSelectedProductsMapped)
  const { leasing_sum, leasing_duration_months, leasing_durations } = useSelector(getOrderTotalCost)

  return (
    selectedProducts.length > 0 && (
      <tfoot>
        {Object.keys(leasing_durations).map(leasing_duration => (
          <tr className="totals" key={leasing_duration}>
            <td colSpan={2}>
              <Translation id="Total monthly {n} months" n={leasing_duration} />
            </td>

            <td className="number">
              <FormattedNumberWithCurrency amount={leasing_durations[leasing_duration]} />
            </td>
          </tr>
        ))}

        <tr className="totals">
          <td colSpan={2}>
            <Translation id="Total {n} months" n={leasing_duration_months} />
          </td>

          <td className="number">
            <FormattedNumberWithCurrency amount={leasing_sum} />
          </td>
        </tr>
      </tfoot>
    )
  )
}

const BuyingTotals = () => {
  const selectedProducts = useSelector(getSelectedProductsMapped)
  const { buying_sum } = useSelector(getOrderTotalCost)

  if (selectedProducts.length === 0) return null

  return (
    <tfoot>
      <tr className="totals">
        <td colSpan={2}>
          <Translation id="Total" />
        </td>

        <td className="number">
          <FormattedNumberWithCurrency amount={buying_sum} />
        </td>
      </tr>
    </tfoot>
  )
}

const Product = ({
  id,
  name,
  buying_price,
  leasing_price,
  leasing_duration_months,
  amount,
}: any) => {
  const dispatch = useDispatch()

  // @ts-ignore
  const setAmount = amount =>
    dispatch(addProduct(createProductSelection({ productId: id, amount })))
  const openDialog = () => dispatch(activatePopup(<ProductRemovalDialog productId={id} />))
  // @ts-ignore
  const onClick = newAmount => (newAmount === 0 ? openDialog() : setAmount(newAmount))
  const isCompleted = useSelector(getIsCompleted)

  const isLeasing = useSelector(getIsLeasing)

  return (
    <tr className="product">
      <td className="product__name">{name}</td>

      <td className="amount">
        <button onClick={() => onClick(parseInt(amount) - 1)} disabled={isCompleted}>
          -
        </button>

        <input
          value={amount}
          onChange={e => setAmount(e.target.value)}
          type="number"
          disabled={isCompleted}
        />

        <button onClick={() => setAmount(parseInt(amount) + 1)} disabled={isCompleted}>
          +
        </button>
      </td>

      <td className="number">
        <FormattedNumberWithCurrency
          amount={(isLeasing ? leasing_price / leasing_duration_months : buying_price) * amount}
        />
      </td>
    </tr>
  )
}

export default compose(withShopProductsLoaded)(QuoteSummary)
